import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  PUSH_CHANGED_EVENT, currentSubscription, enablePush, permission, pushApi, pushSupport, refreshSubscription,
} from '../lib/push'
import { Button, Callout } from './ui'

const DISMISS_KEY = 'ecrm_push_dismissed'
const DISMISS_DAYS = 7

function dismissedRecently(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) ?? 0)
    return Date.now() - at < DISMISS_DAYS * 24 * 3600 * 1000
  } catch { return false }
}

/**
 * Promemoria in cima alle pagine: "Attiva le notifiche sul telefono", con il pulsante che chiede il permesso (su
 * iPhone il permesso si puo' chiedere solo dal tocco di un pulsante). Compare se il flag `notifiche.push` e' acceso,
 * il dispositivo le supporta e su questo dispositivo non sono ancora attive; "Non ora" lo nasconde per 7 giorni. Su
 * iPhone in Safari (CRM non aggiunto alla Home) ci pensa `InstallAppPrompt`. Se il permesso c'e' gia', riscrive in
 * silenzio l'iscrizione per l'utente collegato (stesso telefono, utente diverso).
 */
export function PushPrompt() {
  const on = useFeatureFlag('notifiche.push')
  const navigate = useNavigate()
  const [publicKey, setPublicKey] = useState<string | null>(null)
  const [visible, setVisible] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!on || pushSupport() !== 'supported') return
    let alive = true
    const check = async () => {
      const state = await pushApi.get().catch(() => null)
      if (!alive || !state?.enabled || !state.publicKey) return
      setPublicKey(state.publicKey)
      if (permission() === 'granted') {
        await refreshSubscription(state).catch(() => undefined)
        const sub = await currentSubscription().catch(() => null)
        if (alive) setVisible(!sub && !dismissedRecently())
        return
      }
      setVisible(permission() === 'default' && !dismissedRecently())
    }
    void check()
    const onChange = () => { void check() }
    window.addEventListener(PUSH_CHANGED_EVENT, onChange)
    return () => { alive = false; window.removeEventListener(PUSH_CHANGED_EVENT, onChange) }
  }, [on])

  if (!visible || !publicKey) return null

  const close = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())) } catch { /* solo per questa visita */ }
    setVisible(false)
  }
  const activate = async () => {
    setBusy(true)
    setMessage(null)
    try {
      const result = await enablePush(publicKey)
      if (result === 'denied') {
        setMessage('Permesso negato: per riattivarle consenti le notifiche per Equal CRM nelle impostazioni del telefono o del browser.')
        return
      }
      setVisible(false)
    } catch {
      setMessage('Non è stato possibile attivare le notifiche su questo dispositivo. Riprova da Notifiche → Preferenze.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Callout tone="info" icon="notifications_active" className="mb-3"
      action={<>
        <Button variant="tonal" size="sm" disabled={busy} onClick={() => void activate()}>Attiva notifiche</Button>
        <Button variant="text" size="sm" onClick={() => navigate('/notifications?tab=preferences')}>Scegli quali</Button>
        <Button variant="text" size="sm" onClick={close}>Non ora</Button>
      </>}>
      {message ?? 'Ricevi sul telefono gli aggiornamenti delle tue pratiche (KO, documenti da integrare, attivazioni…) appena succedono.'}
    </Callout>
  )
}
